import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import { CircularProgress, Tooltip } from "@mui/material";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import { styled } from "@mui/material/styles";
import axios from "axios";
import { API_URL } from "const/constants";
import * as React from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import UploadFile from "../../../components/Input/UploadFile";
import StickyNote2Icon from "@mui/icons-material/StickyNote2";
import AttachmentChip from "components/Attachments/AttachmentChip";
import { useDraftAttachmentPaste } from "hooks/useAttachmentUploads";
import DeleteConfirmDialog from "./DeleteConfirmDialog";
import { getNoteDeleteConfirmMessage, isPaymentNote, isSameNote } from "./modulePaymentNoteUtils";
import TermSwapNoteCard from "./TermSwapNoteCard";
import { isTermSwapNote, parseTermSwapNote } from "./termSwapNoteUtils";
import SuperAdminTermEditNoteCard from "./SuperAdminTermEditNoteCard";
import {
  isSuperAdminTermEditNote,
  parseSuperAdminTermEditNote,
} from "./superAdminTermEditNoteUtils";
import { formatDisplayDateTime } from "utils/dateFormat";

const isSystemGeneratedNote = (note) => isTermSwapNote(note) || isSuperAdminTermEditNote(note);

const readUserDetail = () => {
  try {
    const raw = localStorage.getItem("userDetail");
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

const generateNoteUuid = () => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `note-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
};

const noteTimestamp = (value) => {
  const time = new Date(value).getTime();
  return Number.isNaN(time) ? 0 : time;
};

const formatNoteDate = (value) => formatDisplayDateTime(value);

const resolveNotesRow = (accountDetails, { subtable, dataUuid, parentId }) => {
  if (!subtable) {
    const parentIndex = accountDetails.findIndex((item) => item?.uuid === dataUuid);
    if (parentIndex < 0) return null;
    return {
      parentIndex,
      subtableIndex: -1,
      row: accountDetails[parentIndex],
    };
  }

  const parentIndex = accountDetails.findIndex((obj) => obj?.uuid === parentId);
  if (parentIndex < 0) return null;

  const admissionDetails = accountDetails[parentIndex]?.admissionDetails || [];
  const subtableIndex = admissionDetails.findIndex((subItem) => subItem?.uuid === dataUuid);
  if (subtableIndex < 0) return null;

  return {
    parentIndex,
    subtableIndex,
    row: admissionDetails[subtableIndex],
  };
};

const applyNotesChange = (prev, { subtable, dataUuid, parentId }, updateNotes) => {
  if (!subtable) {
    const parentIndex = prev.findIndex((item) => item?.uuid === dataUuid);
    if (parentIndex < 0) {
      console.warn("NotesModal: account row not found for uuid:", dataUuid);
      return prev;
    }

    const row = prev[parentIndex];
    const nextNotes = updateNotes(row?.notes || []);
    return [
      ...prev.slice(0, parentIndex),
      { ...row, notes: nextNotes, notesCount: nextNotes.length },
      ...prev.slice(parentIndex + 1),
    ];
  }

  const parentIndex = prev.findIndex((obj) => obj?.uuid === parentId);
  if (parentIndex < 0) {
    console.warn("NotesModal: parent row not found for parentId:", parentId);
    return prev;
  }

  const admissionDetails = prev[parentIndex]?.admissionDetails || [];
  const subtableIndex = admissionDetails.findIndex((subItem) => subItem?.uuid === dataUuid);
  if (subtableIndex < 0) {
    console.warn("NotesModal: subtable row not found for uuid:", dataUuid);
    return prev;
  }

  const row = admissionDetails[subtableIndex];
  const nextNotes = updateNotes(row?.notes || []);
  const nextSubRow = { ...row, notes: nextNotes, notesCount: nextNotes.length };

  return [
    ...prev.slice(0, parentIndex),
    {
      ...prev[parentIndex],
      admissionDetails: [
        ...admissionDetails.slice(0, subtableIndex),
        nextSubRow,
        ...admissionDetails.slice(subtableIndex + 1),
      ],
    },
    ...prev.slice(parentIndex + 1),
  ];
};

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
}));

export default function NotesModal({
  open,
  handleClose,
  data,
  accountDetails,
  setAccountDetails,
  subtable,
  parentId,
  onLocalNoteAdded,
  onPaymentNoteDeleted,
  // Standalone mode: notes are read from `initialNotes` and written straight to the given parent
  // (used for admission-term notes, which are anchored to the term row rather than to a row in
  // `accountDetails`). `onChanged` lets the caller refetch after add/delete.
  standalone = false,
  standaloneParentType,
  standaloneParentId,
  initialNotes,
  onChanged,
}) {
  const [text, setText] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [sending, setSending] = useState(false);
  const { isUploading, onPaste: handlePaste } = useDraftAttachmentPaste({
    draft: attachments,
    setDraft: setAttachments,
  });
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, note: null, loading: false });
  const [standaloneNotes, setStandaloneNotes] = useState([]);
  const buttonRef = useRef(null);
  const userDetail = useMemo(() => readUserDetail(), [open]);
  const currentUserLabel = userDetail?.name ?? userDetail?.firstName ?? "";

  useEffect(() => {
    if (standalone && open) {
      setStandaloneNotes(Array.isArray(initialNotes) ? initialNotes : []);
    }
  }, [standalone, open, initialNotes]);

  const parentType = subtable ? "ADMISSION_DETAIL" : "ACCOUNT_DETAIL";

  const parentObject = useMemo(
    () =>
      standalone
        ? { notes: standaloneNotes }
        : resolveNotesRow(accountDetails, {
            subtable,
            dataUuid: data?.uuid,
            parentId,
          })?.row ?? null,
    [standalone, standaloneNotes, accountDetails, subtable, data?.uuid, parentId]
  );

  const updateLocalNotes = (newNote) => {
    const located = resolveNotesRow(accountDetails, {
      subtable,
      dataUuid: data?.uuid,
      parentId,
    });
    if (!located) {
      toast.error("Could not find this row to save the note.");
      return;
    }

    setAccountDetails((prev) =>
      applyNotesChange(prev, { subtable, dataUuid: data?.uuid, parentId }, (notes) => [
        ...notes,
        newNote,
      ])
    );
  };

  const submitPost = async (e) => {
    e.preventDefault();
    const attachmentUrls =
      attachments?.attachments?.length > 0
        ? `[ATTACHMENTS]${attachments?.attachments?.join("|")}[/ATTACHMENTS]`
        : "";
    const combinedText = `${text?.trim()}${attachmentUrls}`;

    if (standalone) {
      if (standaloneParentId == null) {
        toast.error("Could not save the note — missing parent reference.");
        return;
      }
      setSending(true);
      try {
        const formData = new FormData();
        formData.append("parentId", standaloneParentId);
        formData.append("parentType", standaloneParentType);
        formData.append("noteText", combinedText);
        const response = await axios.post(`${API_URL}/notes`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        const savedNote = response.data?.data;
        if (savedNote) {
          setStandaloneNotes((prev) => [...prev, savedNote]);
        }
        onChanged?.();
      } catch (err) {
        console.error("Failed to save note", err);
        toast.error("Failed to save note");
        return;
      } finally {
        setSending(false);
      }
      setText("");
      setAttachments([]);
      return;
    }

    if (data?.id) {
      setSending(true);
      try {
        const formData = new FormData();
        formData.append("parentId", data.id);
        formData.append("parentType", parentType);
        formData.append("noteText", combinedText);

        const response = await axios.post(`${API_URL}/notes`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });

        const savedNote = response.data?.data;
        updateLocalNotes(savedNote);
      } catch (err) {
        console.error("Failed to save note", err);
        toast.error("Failed to save note");
        return;
      } finally {
        setSending(false);
      }
    } else {
      const newNote = {
        text: combinedText,
        uuid: generateNoteUuid(),
        parentId: data?.uuid,
        createdBy: currentUserLabel || userDetail?.email,
        createdDate: new Date().toISOString(),
      };
      updateLocalNotes(newNote);
      onLocalNoteAdded?.();
    }

    setText("");
    setAttachments([]);
  };

  const parseNoteContent = (noteText) => {
    const attachmentMatch = noteText?.match(/\[ATTACHMENTS\](.*?)\[\/ATTACHMENTS\]/);
    if (attachmentMatch) {
      const attachments = attachmentMatch[1].split("|");
      const text = noteText.replace(/\[ATTACHMENTS\].*?\[\/ATTACHMENTS\]/, "").trim();
      return { text, attachments };
    }
    return { text: noteText, attachments: [] };
  };

  const requestDeleteComment = (note) => {
    setDeleteConfirm({ open: true, note, loading: false });
  };

  const cancelDeleteComment = () => {
    setDeleteConfirm({ open: false, note: null, loading: false });
  };

  const performDeleteComment = async () => {
    const note = deleteConfirm.note;
    if (!note) return;

    setDeleteConfirm((prev) => ({ ...prev, loading: true }));

    if (standalone) {
      try {
        if (note.id) {
          // Union list may mix stores (term vs legacy installment notes) — delete against the
          // note's own parent type, falling back to the modal's default.
          const noteParentType = note.parentType || standaloneParentType;
          await axios.delete(`${API_URL}/notes/${note.id}?parentType=${noteParentType}`);
        }
        setStandaloneNotes((prev) => prev.filter((n) => !isSameNote(n, note)));
        onChanged?.();
        cancelDeleteComment();
      } catch (err) {
        console.error("Failed to delete note", err);
        toast.error(err?.response?.data?.errorMessage || "Failed to delete note");
        setDeleteConfirm((prev) => ({ ...prev, loading: false }));
      }
      return;
    }

    try {
      if (note.id) {
        await axios.delete(`${API_URL}/notes/${note.id}?parentType=${parentType}`);
      }

      await onPaymentNoteDeleted?.(note);

      const located = resolveNotesRow(accountDetails, {
        subtable,
        dataUuid: data?.uuid,
        parentId,
      });
      if (!located) {
        toast.error("Could not find this row to delete the note.");
        setDeleteConfirm((prev) => ({ ...prev, loading: false }));
        return;
      }

      setAccountDetails((prev) =>
        applyNotesChange(prev, { subtable, dataUuid: data?.uuid, parentId }, (notes) =>
          notes.filter((n) => !isSameNote(n, note))
        )
      );

      cancelDeleteComment();
    } catch (err) {
      console.error("Failed to delete note", err);
      toast.error(err?.response?.data?.errorMessage || "Failed to delete note");
      setDeleteConfirm((prev) => ({ ...prev, loading: false }));
    }
  };

  return (
    <React.Fragment>
      <BootstrapDialog
        open={open}
        maxWidth={"md"}
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: "90vh",
            position: "relative",
          },
        }}
      >
        <form className="flex flex-col h-full" onSubmit={submitPost}>
          <DialogTitle
            sx={{
              m: 0,
              p: 2,
              borderBottom: "1px solid #e5e7eb",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              position: "sticky",
              top: 0,
              backgroundColor: "background.paper",
              zIndex: 1,
            }}
            id="customized-dialog-title"
          >
            <div className="flex items-center gap-2">
              <StickyNote2Icon className="text-orange-400" />
              <span className="text-xl font-semibold">Notes</span>
            </div>
            <IconButton
              aria-label="close"
              onClick={handleClose}
              sx={{
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <DialogContent
            dividers
            sx={{
              p: 0,
              display: "flex",
              flexDirection: "column",
              flex: 1,
              overflow: "hidden",
            }}
          >
            <div className="flex flex-col h-full">
              {!standalone && !data?.id && (
                <div className="flex items-center gap-2 bg-amber-50 border-b border-amber-200 px-4 py-2 text-amber-800 text-sm">
                  <span className="font-semibold">⚠</span>
                  <span>
                    Notes added here will be saved when you click <strong>Save All Changes</strong>{" "}
                    in the account entries.
                  </span>
                </div>
              )}
              <div className="flex-1 overflow-y-auto p-4">
                {parentObject?.notes?.length > 0 ? (
                  <div className="space-y-4">
                    {[...(parentObject?.notes || [])]
                      .sort((a, b) => noteTimestamp(a?.createdDate) - noteTimestamp(b?.createdDate))
                      .map((arg) => {
                        const { text, attachments } = parseNoteContent(arg?.text);
                        const termSwap = parseTermSwapNote(text);
                        const superAdminEdit = !termSwap ? parseSuperAdminTermEditNote(text) : null;
                        const noteBody = termSwap ? (
                          <TermSwapNoteCard swap={termSwap} />
                        ) : superAdminEdit ? (
                          <SuperAdminTermEditNoteCard edit={superAdminEdit} />
                        ) : (
                          <p className="whitespace-pre-wrap break-words">{text}</p>
                        );
                        const structuredNote = Boolean(termSwap || superAdminEdit);
                        return (
                          <div
                            id="chatbox"
                            className="group relative"
                            key={arg?.uuid || arg?.id || arg?.createdDate}
                          >
                            {currentUserLabel && currentUserLabel === arg?.createdBy ? (
                              <div className="flex flex-col items-end">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="text-sm text-app-text-secondary">
                                    {formatNoteDate(arg?.createdDate)}
                                  </span>
                                  {!isSystemGeneratedNote(arg) && (
                                    <Tooltip title="Delete this comment">
                                      <IconButton
                                        size="small"
                                        onClick={() => requestDeleteComment(arg)}
                                        className="opacity-0 group-hover:opacity-100 transition-opacity"
                                      >
                                        <DeleteIcon sx={{ fontSize: 16 }} />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                </div>
                                <div className="flex flex-col items-end gap-2">
                                  <div
                                    className={
                                      structuredNote
                                        ? ""
                                        : "bg-blue-500 text-white rounded-2xl py-2 px-4"
                                    }
                                  >
                                    {noteBody}
                                  </div>
                                  {attachments?.length > 0 && (
                                    <div className="flex flex-wrap gap-2 justify-end mt-1 w-fit">
                                      {attachments.map((attachment, index) => (
                                        <AttachmentChip key={index} url={attachment} />
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </div>
                            ) : (
                              <div className="flex flex-col">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="text-sm font-medium text-app-text">
                                    {arg?.createdBy}
                                  </span>
                                  <span className="text-sm text-app-text-secondary">
                                    {formatNoteDate(arg?.createdDate)}
                                  </span>
                                </div>
                                <div className="flex flex-col gap-2">
                                  <div
                                    className={
                                      structuredNote
                                        ? ""
                                        : "bg-app-surface-muted rounded-2xl py-2 px-4 w-fit"
                                    }
                                  >
                                    {structuredNote ? (
                                      noteBody
                                    ) : (
                                      <p className="whitespace-pre-wrap break-words text-app-text">
                                        {text}
                                      </p>
                                    )}
                                  </div>
                                  {attachments?.length > 0 && (
                                    <div className="flex flex-wrap gap-2 mt-1">
                                      {attachments.map((attachment, index) => (
                                        <AttachmentChip key={index} url={attachment} />
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center h-full text-app-text-secondary">
                    <StickyNote2Icon sx={{ fontSize: 48, color: "gray" }} />
                    <p className="mt-4 text-lg">No notes yet</p>
                    <p className="text-sm">Start the conversation by adding a note</p>
                  </div>
                )}
                <div ref={buttonRef} />
              </div>
            </div>
          </DialogContent>
          <DialogActions
            sx={{
              position: "sticky",
              bottom: 0,
              backgroundColor: "background.paper",
              zIndex: 1,
            }}
          >
            <div className="flex flex-col w-full">
              <div className="border-t border-app-border p-4 bg-app-surface-muted">
                <div className="space-y-4">
                  <div className="bg-app-surface rounded-lg border border-app-border">
                    <textarea
                      id="note"
                      rows="3"
                      className="w-full px-4 py-3 text-app-text border-0 focus:ring-0 focus:outline-none"
                      placeholder="Write a note... or paste files to attach them"
                      onChange={(e) => setText(e.target.value)}
                      onPaste={handlePaste}
                      value={text}
                    />
                  </div>

                  <div className="bg-app-surface rounded-lg border border-app-border p-4">
                    <UploadFile
                      data={attachments}
                      setData={setAttachments}
                      imageKey="attachments"
                      label="Attachments"
                      type="multiple"
                      newDesign={true}
                    />
                  </div>

                  <div className="flex justify-end"></div>
                </div>
              </div>
              <Button
                variant="contained"
                type="submit"
                disabled={
                  sending ||
                  isUploading ||
                  (!text && (!attachments?.attachments || attachments.attachments.length === 0))
                }
                startIcon={
                  sending || isUploading ? <CircularProgress size={16} color="inherit" /> : null
                }
                sx={{
                  px: 4,
                  py: 1,
                  borderRadius: 2,
                  textTransform: "none",
                  fontSize: "1rem",
                }}
              >
                {sending ? "Sending..." : isUploading ? "Uploading..." : "Send Note"}
              </Button>
            </div>
          </DialogActions>
        </form>
      </BootstrapDialog>
      <DeleteConfirmDialog
        open={deleteConfirm.open}
        title={
          deleteConfirm.note && isPaymentNote(deleteConfirm.note)
            ? "Delete payment note?"
            : "Delete note?"
        }
        message={deleteConfirm.note ? getNoteDeleteConfirmMessage(deleteConfirm.note) : ""}
        confirmLabel="Delete"
        onConfirm={performDeleteComment}
        onCancel={cancelDeleteComment}
        loading={deleteConfirm.loading}
      />
    </React.Fragment>
  );
}
